import { useMemo, useState } from "react"
import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom"
import { Separator } from "@/components/ui/separator"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { useWorkspace } from "@/workspaces/WorkspaceContext"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowLeft01Icon,
  CheckmarkCircle01Icon,
  HardDriveIcon,
  Mic01Icon,
  Notification01Icon,
  Search01Icon,
  Settings05Icon,
  ShieldUserIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserMultiple02Icon,
  Wallet01Icon,
} from "@hugeicons/core-free-icons"
import { cn } from "@/lib/utils"

export function SettingsLayout() {
  const { workspace_slug } = useParams()
  const location = useLocation()
  const { hasPermission } = useWorkspace()
  const [query, setQuery] = useState("")
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const base = `/${workspace_slug}/settings`
  const canManageBilling = hasPermission("workspace:billing")

  const navGroups = useMemo(
    () => [
      {
        group: "Личное",
        items: [
          { to: "profile", label: "Профиль", icon: UserCircleIcon, description: "Личные данные и предпочтения" },
          { to: "notifications", label: "Уведомления", icon: Notification01Icon, description: "Каналы и частота уведомлений" },
        ],
      },
      {
        group: "Рабочая область",
        items: [
          { to: "general", label: "Общие", icon: Settings05Icon, description: "Название, URL и логотип" },
          { to: "members", label: "Участники", icon: UserGroupIcon, description: "Участники и доступ" },
          { to: "teams", label: "Команды", icon: UserMultiple02Icon, description: "Команды и структура" },
          { to: "roles", label: "Роли", icon: ShieldUserIcon, description: "Роли и права" },
          { to: "status-schemes", label: "Статусы", icon: CheckmarkCircle01Icon, description: "Схемы статусов" },
          { to: "storage", label: "Хранилище", icon: HardDriveIcon, description: "Файлы и лимиты" },
          {
            to: "meetings",
            label: "Митинги",
            icon: Mic01Icon,
            description: "Запись и транскрипция встреч",
          },
          ...(canManageBilling
            ? [
                {
                  to: "billing",
                  label: "Тариф и оплата",
                  icon: Wallet01Icon,
                  description: "Тариф, пользователи и хранилище",
                },
              ]
            : []),
        ],
      },
    ],
    [canManageBilling],
  )

  const filteredGroups = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return navGroups
    return navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          `${item.label} ${item.description}`.toLowerCase().includes(normalized),
        ),
      }))
      .filter((group) => group.items.length > 0)
  }, [navGroups, query])

  const currentLabel =
    navGroups.flatMap((group) => group.items).find((item) => location.pathname.endsWith(`/${item.to}`))?.label ?? "Настройки"

  const settingsNav = (
    <>
      <div className="border-b px-3 py-3">
        <div className="relative">
          <HugeiconsIcon
            icon={Search01Icon}
            strokeWidth={2}
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Поиск настроек..."
            className="h-11 rounded-md bg-background pl-9 text-sm shadow-none lg:h-9"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <nav className="space-y-4 px-2 py-3">
          {filteredGroups.map((group) => (
            <div key={group.group}>
              <p className="px-2 pb-1.5 text-xs font-medium text-muted-foreground">
                {group.group}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={`${base}/${item.to}`}
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "block rounded-md px-2.5 py-2 transition-colors max-lg:min-h-11",
                        isActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <div className="flex items-start gap-3">
                        <span
                          className={cn(
                            "mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-md",
                            isActive ? "text-sidebar-accent-foreground" : "text-muted-foreground",
                          )}
                        >
                          <HugeiconsIcon icon={item.icon} strokeWidth={2} className="size-4" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium">{item.label}</span>
                          <span className="block text-xs text-muted-foreground">
                            {item.description}
                          </span>
                        </span>
                      </div>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>
    </>
  )

  return (
    <div className="flex h-svh min-h-0 overflow-hidden bg-background text-foreground">
      <aside className="hidden w-[280px] shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground lg:flex">
        <div className="flex h-[43px] items-center gap-2 border-b px-3">
          <Link
            to={`/${workspace_slug}/home`}
            className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            aria-label="Назад"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} strokeWidth={2} className="size-4" />
          </Link>
          <Separator orientation="vertical" className="h-5" />
          <div className="min-w-0 text-sm font-medium">Настройки</div>
        </div>

        {settingsNav}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col bg-background">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between gap-3 border-b bg-background px-3 pt-[env(safe-area-inset-top)] lg:h-[43px] lg:justify-start lg:px-4 lg:pt-0">
          <Link
            to={`/${workspace_slug}/home`}
            className="inline-flex size-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:hidden"
            aria-label="Назад"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} strokeWidth={2} className="size-5" />
          </Link>
          <div className="min-w-0 truncate text-sm font-medium text-foreground lg:hidden">{currentLabel}</div>
          <div className="hidden text-sm text-muted-foreground lg:block">
            Настройки <span className="mx-1">/</span> <span className="text-foreground">{currentLabel}</span>
          </div>
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="h-11 gap-2 px-3 lg:hidden" aria-label="Разделы настроек">
                <HugeiconsIcon icon={Settings05Icon} strokeWidth={2} className="size-5" />
                <span className="text-sm">Разделы</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="h-svh w-screen max-w-none gap-0 bg-sidebar p-0 text-sidebar-foreground">
              <SheetHeader className="border-b px-4 py-3">
                <SheetTitle>Настройки</SheetTitle>
              </SheetHeader>
              {settingsNav}
            </SheetContent>
          </Sheet>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="mx-auto max-w-5xl px-3 py-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:px-5 md:px-6 lg:px-8 lg:py-8">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  )
}

export function SettingsSection({
  title,
  description,
  action,
  children,
}: {
  title: string
  description?: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="space-y-4 rounded-lg bg-card px-4 py-4 text-sm text-card-foreground ring-1 ring-foreground/10 lg:rounded-xl">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-base font-medium leading-snug">{title}</h2>
          {description ? <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p> : null}
        </div>
        {action ? <div className="flex shrink-0">{action}</div> : null}
      </div>
      <Separator />
      {children}
    </section>
  )
}
